import { useMemo } from 'react';
import { differenceInDays } from 'date-fns';
import { InitiatePayload } from 'flows/core/types/cards';
import { isTaskCompletedOrValidatingOrPending } from 'flows/core/utils/card-makers/utils';

export const useDaysLeft = (
  currentMoveDate: string | null | undefined
): number => {
  return useMemo(() => {
    if (!currentMoveDate) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const [year, month, day] = currentMoveDate.split('-').map(Number);
    const targetDate = new Date(year, month - 1, day);

    return differenceInDays(targetDate, today);
  }, [currentMoveDate]);
};

export const getDaysText = (daysLeft: number): string => {
  if (daysLeft === 0) return "It's move-in day!";
  if (daysLeft === 1) return "Tomorrow's the big day!";
  if (daysLeft < 0) return 'Welcome home!';
  return `${daysLeft} days left until your move`;
};

export interface ProgressTotals {
  totalTasks: number;
  completedTasks: number;
  pct: number;
}

export const useProgressTotals = (
  priorityCards: InitiatePayload[] | undefined,
  completedCards: InitiatePayload[] | undefined
): ProgressTotals => {
  return useMemo(() => {
    const completedRequiredCount = (completedCards || []).filter(
      (card: InitiatePayload) =>
        isTaskCompletedOrValidatingOrPending(card.data) &&
        (card.data?.required || card.data?.priority)
    ).length;

    const totalTasks = (priorityCards?.length || 0) + completedRequiredCount;
    const completedTasks =
      (priorityCards?.filter((card: InitiatePayload) =>
        isTaskCompletedOrValidatingOrPending(card.data)
      ).length || 0) + completedRequiredCount;

    const pct =
      totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return { totalTasks, completedTasks, pct };
  }, [priorityCards, completedCards]);
};
